import Cookie from '@wetransfer/concorde-cookie';

import { actions } from '@app/action/transfer';
import Helper from '@app/helper';
import Store from '@app/store';

import { convertExpiry } from '@module/transfer/build';
import Transfer from '@module/transfer';

import { isDefaultRecipientInUploadLocked } from '@helper/experiments';
import FileEntry from '@app/modules/transfer/file-entry';

function getDefaultRecipient(user, account, channel) {
  const channelName = channel.name || '';
  const channelEmail = channel.defaultRecipient || false;

  // User is logged in but not on it's own channel and there is a channel email available
  if (user.loggedIn && channelName !== account.subdomain_name && channelEmail) {
    return channelEmail;
  }
  // User is not logged in but a channel email is available
  if (!user.loggedIn && channelEmail) {
    return channelEmail;
  }

  return false;
}

const checkExpiry = (user) =>
  user &&
  user.preferences &&
  user.preferences.transfer_default_expiry !== undefined;

const isSendingToDefaultRecipient = (transfer = false) => {
  const {
    channel: { defaultRecipient },
  } = Store.getState();
  if (!defaultRecipient || !transfer) {
    return false;
  }
  if (transfer.recipients.length !== 1) {
    return false;
  }
  if (transfer.type !== 'email') {
    return false;
  }
  if (transfer.recipients[0] !== defaultRecipient) {
    return false;
  }
  return true;
};

const getUserType = (transfer = false) => {
  const { user } = Store.getState();
  if (user.loggedIn) {
    return 'plus';
  }
  if (!transfer) {
    return 'free';
  }
  if (isSendingToDefaultRecipient(transfer)) {
    return 'plus';
  }
  return 'free';
};

const getAllowedSize = (transfer = false) => {
  const {
    channel: { inboundTransferSizeLimit },
    account,
    user,
  } = Store.getState();
  // Unlimited transfer size feature
  // * if a user is logged in
  // * if account has no transfer_limite (i.e -1)
  if (user.loggedIn && account.transfer_limit === -1) {
    return -1;
  }
  if (!inboundTransferSizeLimit || !transfer) {
    return;
  }
  if (!isSendingToDefaultRecipient(transfer)) {
    return;
  }
  return inboundTransferSizeLimit;
};

const setAllowedSizeAndUserType = (transfer) => {
  transfer.userType = getUserType(transfer);
  transfer.allowedSize = getAllowedSize(transfer);
  return transfer;
};

const createTransfer = () => {
  const { user, channel, account } = Store.getState();
  // Get the last used sender from a cookie
  const defaultSender = Helper.getLastUsedSender();
  const defaultRecipient = getDefaultRecipient(user, account, channel);

  // Get the last used type from a cookie
  let defaultType = Cookie.get('wt_ttype');
  if (defaultType === '4') {
    defaultType = 'link';
  }
  if (defaultType === '3') {
    defaultType = 'email';
  }
  if (defaultType === '2') {
    defaultType = 'email';
  }
  if (defaultType === '1') {
    defaultType = 'email';
  }

  // We only want to allow email transfers when the default recipient is locked in profile pages
  if (
    isDefaultRecipientInUploadLocked(
      channel.defaultRecipient,
      user.loggedIn,
      channel.name,
      account.subdomain_name
    )
  ) {
    defaultType = 'email';
  }

  // Create a new Transfer object
  const newTransfer = new Transfer({
    userType: getUserType(),
    type: defaultType === 'link' ? 'link' : 'email',
    onChannel: !!channel.name,
    accountId: account.id,
  });

  // Should we fill in the sender?
  if (defaultSender) {
    newTransfer.sender = defaultSender;
  }

  // Should we add a default recipient?
  if (defaultRecipient) {
    newTransfer.recipients.push(defaultRecipient);
  }

  // Assign channel name to transfer
  if (channel.name) {
    newTransfer.channel = channel.name;
  }

  // If there is a default expiry available, please use it.
  if (checkExpiry(user)) {
    newTransfer.expiry = convertExpiry(
      user.preferences.transfer_default_expiry
    );
  }

  return setAllowedSizeAndUserType(newTransfer, account);
};

const similarTransfer = (transfer) => {
  const { account, channel, user } = Store.getState();

  const newTransfer = new Transfer({
    userType: getUserType(),
    type: transfer.type,
    accountId: account.id,
  });

  if (transfer.sender) {
    newTransfer.sender = transfer.sender;
  }

  if (transfer.channel) {
    newTransfer.channel = transfer.channel;
  }

  if (transfer.expiry) {
    newTransfer.expiry = transfer.expiry;
  }

  if (
    isDefaultRecipientInUploadLocked(
      channel.defaultRecipient,
      user.loggedIn,
      channel.name,
      account.subdomain_name
    )
  ) {
    newTransfer.recipients = transfer.recipients;
  }

  return setAllowedSizeAndUserType(newTransfer);
};

const handleError = (transfer, error) => {
  if (
    ['LimitReached', 'UserBlocked', 'UnavailableParent'].indexOf(error) !== -1
  ) {
    transfer.lastError = error;
  }

  return transfer;
};

const forwardTransfer = (transfer, state) => {
  const {
    account,
    user,
    channel,
    transfer: currentTransfer,
  } = Store.getState();

  // A forward is kind of a similar transfer
  const forward = similarTransfer(transfer);

  // Set our parent id and message
  forward.parent_id = transfer.id;
  forward.message = transfer.message || '';

  // Is there an account id?
  if (account.id) {
    forward.accountId = account.id;
  }

  // If it's a link we'll just switch back to e-mail
  // and use last sender from cookie.
  if (forward.type === 'link') {
    forward.sender = Helper.getLastUsedSender();
    forward.type = 'email';
  }

  // Sometimes we still haven't filled in a sender
  // even though we DO have one. Let's use that!
  if (state.sender && !forward.sender) {
    forward.sender = state.sender;
  }

  if (
    isDefaultRecipientInUploadLocked(
      channel.defaultRecipient,
      user.loggedIn,
      channel.name,
      account.subdomain_name
    )
  ) {
    forward.recipients = currentTransfer.recipients;
  }

  // If there is a default expiry available, please use it.
  if (checkExpiry(user)) {
    forward.expiry = convertExpiry(user.preferences.transfer_default_expiry);
  }

  // And finally add the files
  forward.add(transfer.files); // NOTE: this can fail but will soft-fail because async
  return forward;
};

const updateUserState = (state, type) => {
  const { user } = Store.getState();
  let expiry = false;
  // If there is a default expiry available, please use it.
  if (checkExpiry(user)) {
    expiry = convertExpiry(user.preferences.transfer_default_expiry);
  }

  state.setUserType(type === 'plus' ? 'plus' : 'free', expiry);
};

const findFile = (file_id) => (file) => file.id === file_id;

export default function(state = {}, action) {
  const { type } = action;
  switch (type) {
    case actions.TRANSFER_BUILD: {
      const transferDetails = { ...action.params };
      transferDetails.items = (transferDetails.items || []).map(
        ({ file, item_type, ...item }) => ({
          ...item,
          file: file || new FileEntry(item),
          apiType: item_type,
        })
      );
      return Transfer.buildFromParams(
        { ...transferDetails, userType: getUserType() },
        action.password
      );
    }
    case actions.TRANSFER_CREATE:
      return createTransfer();

    case actions.TRANSFER_CREATE_SIMILAR:
      return similarTransfer(action.transfer);

    case actions.TRANSFER_CREATE_FORWARD:
      return forwardTransfer(action.transfer, state);

    case actions.TRANSFER_CHANGE_TYPE:
      state.type = action.transferType;
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_CHANGE_EXPIRY:
      state.expiry = action.period;
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_CHANGE_PASSWORD:
      state.password = action.password;
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_CHANGE_USERTYPE:
      if (!state.state) {
        return state;
      }
      updateUserState(state, action.userType);
      return state;

    case actions.TRANSFER_CHANGE_ACCOUNT_ID:
      state.accountId = action.accountId;
      return state;

    case actions.TRANSFER_SET_SENDER:
      state.sender = action.email;
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_SET_MESSAGE:
      state.message = action.message;
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_ADD_RECIPIENT:
      for (const email of action.emails) {
        if (state.recipients.indexOf(email) === -1) {
          state.recipients.push(email);
        }
      }
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_ADD_FILES:
      // This happens in the action
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_REMOVE_FILE:
      state.remove(action.fileName);
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_REMOVE_DIRECTORY:
      state.removeDirectory(action.directoryName);
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_REMOVE_RECIPIENT:
      state.recipients = state.recipients.filter(
        (recipient) => recipient !== action.email
      );
      return setAllowedSizeAndUserType(state);

    case actions.TRANSFER_PAUSE:
      state.pause();
      return state;

    case actions.TRANSFER_RESUME:
      state.resume();
      return state;

    case actions.TRANSFER_CANCEL:
      state.cancel();
      return state;

    case actions.TRANSFER_RESET:
      state.reset();
      return state;

    case actions.TRANSFER_ABORT:
      state.abort(!!action.browserExit);
      return state;

    case actions.TRANSFER_START:
      return state;

    case actions.TRANSFER_STATE_DOWNLOADABLE:
      state.state = 'downloadable';
      return state;

    case actions.TRANSFER_STATE_DOWNLOADING:
      state.state = 'downloading';
      return state;

    case actions.TRANSFER_STATE_FINISHED:
      state.state = 'finished';
      return state;

    case actions.RECAPTCHA_CHALLENGE_RECEIVED:
      state.id = action.transfer_id;
      state.state = 'recaptcha';
      return state;

    case actions.UPLOAD_ERROR:
      return handleError(state, action.error);

    case actions.TRANSFER_STATE_DOWNLOADING_FILE:
      state.items.find(
        (item) => item.id === action.file_id
      ).file.state.download = 'downloading';
      return state;

    case actions.TRANSFER_STATE_FINISHED_FILE:
      state.items.find(
        (item) => item.id === action.file_id
      ).file.state.download = 'done';
      return state;

    case actions.TRANSFER_RESET_DOWNLOAD:
      state.state = 'downloadable';
      return state;

    case actions.TRANSFER_DOWNLOAD_INVALID:
      state.state = 'invalid';
      return state;

    case actions.TRANSFER_DOWNLOAD_EXPIRED:
      state.state = 'expired';
      return state;

    case actions.PREVIEW_LOADING: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = false;
      item.file.state.loaded = false;
      item.file.previewUri = action.file_url;

      return state;
    }

    case actions.PREVIEW_LOADED: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = false;
      item.file.state.loaded = true;

      return state;
    }

    case actions.UPLOAD_NEED_VERIFICATION: {
      state.verification_required = true;
      return state;
    }

    case actions.UPLOAD_VERIFIED: {
      state.verification_required = false;
      return state;
    }

    case actions.PREVIEW_ERROR: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = true;
      item.file.state.loaded = false;
      item.file.previewUri = null;

      return state;
    }

    case actions.TRANSFER_STATE_FEEDBACK:
      state.state = 'feedback';
      return state;

    case actions.TRANSFER_STATE_FEEDBACK_COMPLETE:
      state.state = 'feedbackComplete';
      return state;
  }

  return state;
}
